/**
 * JSOND-to-HTML and JSOND-to-Markdown rendering engines.
 */

import { escapeHtml, enhanceDocText, autoLinkUrls } from './formatting.js';

function getEffectiveFormat(node, index) {
	if (!node.format) return null;
	if (typeof node.format === 'string') return node.format;
	if (Array.isArray(node.format)) return node.format[index] || 'text';
	return null;
}

function renderTableHtml(rows) {
	if (rows.length === 0) return '';
	var html = '<table class="jsond-table"><thead><tr>';
	var headerCells = rows[0].split('|').map(function (c) { return c.trim(); });
	headerCells.forEach(function (cell) {
		html += '<th>' + escapeHtml(cell) + '</th>';
	});
	html += '</tr></thead><tbody>';
	for (var r = 1; r < rows.length; r++) {
		var cells = rows[r].split('|').map(function (c) { return c.trim(); });
		html += '<tr>';
		cells.forEach(function (cell) {
			html += '<td>' + enhanceDocText(escapeHtml(cell), 'html') + '</td>';
		});
		html += '</tr>';
	}
	html += '</tbody></table>';
	return html;
}

export function renderJsondToHtml(doc, compareDoc) {
	if (!doc || !doc.root) return '<p>Invalid JSOND format</p>';

	function processNode(node, compareNode) {
		var html = '';
		var level = node.level || 1;
		var depthClass = Math.min(level, 5);
		var isResearch = node.id === 'research' || node.heading === 'Additional Research';
		var extraClass = isResearch ? ' research-section' : '';

		html += '<div class="jsond-entry depth-' + depthClass + extraClass + '">';

		var headingChanged = compareNode && node.heading !== compareNode.heading;
		html += '<div class="jsond-key' + (headingChanged ? ' session-diff' : '') + '">' + escapeHtml(node.heading || '') + '</div>';

		if (node.body && Array.isArray(node.body) && node.body.length > 0) {
			html += '<div class="jsond-value jsond-markdown">';

			// Check if entire node has a single format string
			var wholeNodeFormat = (typeof node.format === 'string') ? node.format : null;

			// Whole-node diagram: render as Mermaid container (client-side rendering)
			if (wholeNodeFormat === 'diagram') {
				html += '<div class="jsond-diagram"><pre class="mermaid">' + escapeHtml(node.body.join('\n')) + '</pre></div>';
			}
			// Whole-node code: join all body lines into one code block
			else if (wholeNodeFormat === 'code') {
				html += '<pre class="jsond-code"><code>' + escapeHtml(node.body.join('\n')) + '</code></pre>';
			}
			// Whole-node table: render as HTML table
			else if (wholeNodeFormat === 'table') {
				html += renderTableHtml(node.body);
			}
			// Whole-node list or ordered
			else if (wholeNodeFormat === 'list' || wholeNodeFormat === 'ordered') {
				var tag = wholeNodeFormat === 'ordered' ? 'ol' : 'ul';
				html += '<' + tag + '>';
				node.body.forEach(function (paragraph, index) {
					var paraChanged = compareNode && (!compareNode.body || index >= compareNode.body.length || paragraph !== compareNode.body[index]);
					// Strip leading prefixes (defense in depth -- model may write "1. Item" or "- Item" despite format handling)
					var cleanParagraph = wholeNodeFormat === 'ordered' ? paragraph.replace(/^\d+\.\s+/, '') :
						wholeNodeFormat === 'list' ? paragraph.replace(/^[-*]\s+/, '') : paragraph;
					html += '<li' + (paraChanged ? ' class="session-diff"' : '') + '>' + enhanceDocText(escapeHtml(cleanParagraph), 'html') + '</li>';
				});
				html += '</' + tag + '>';
			}
			// Per-element format (array) or no format (auto-detect)
			else {
				var hasPerElementFormat = Array.isArray(node.format);

				// Auto-detect table: if most body elements have 2+ pipes, render as table
				// Fires regardless of format array — catches mislabeled table data
				var pipeRows = node.body.filter(function (p) { return (p.match(/\|/g) || []).length >= 2; });
				if (node.body.length >= 2 && pipeRows.length >= 2 && pipeRows.length >= node.body.length * 0.6) {
					html += renderTableHtml(node.body);
					// skip the per-element loop
				} else {
					// Accumulate consecutive same-type elements for grouping
					var i = 0;
					while (i < node.body.length) {
						var fmt = hasPerElementFormat ? getEffectiveFormat(node, i) : null;
						var paraChanged = compareNode && (!compareNode.body || i >= compareNode.body.length || node.body[i] !== compareNode.body[i]);

						if (fmt === 'code') {
							// Collect consecutive code elements
							var codeLines = [];
							while (i < node.body.length && getEffectiveFormat(node, i) === 'code') {
								codeLines.push(node.body[i]);
								i++;
							}
							html += '<pre class="jsond-code"><code>' + escapeHtml(codeLines.join('\n')) + '</code></pre>';
						} else if (fmt === 'list' || fmt === 'ordered') {
							var listTag = fmt === 'ordered' ? 'ol' : 'ul';
							html += '<' + listTag + '>';
							while (i < node.body.length && getEffectiveFormat(node, i) === fmt) {
								var lpc = compareNode && (!compareNode.body || i >= compareNode.body.length || node.body[i] !== compareNode.body[i]);
								// Strip leading prefixes from list/ordered items (defense in depth)
								var listItem = fmt === 'ordered' ? node.body[i].replace(/^\d+\.\s+/, '') :
									fmt === 'list' ? node.body[i].replace(/^[-*]\s+/, '') : node.body[i];
								html += '<li' + (lpc ? ' class="session-diff"' : '') + '>' + enhanceDocText(escapeHtml(listItem), 'html') + '</li>';
								i++;
							}
							html += '</' + listTag + '>';
						} else if (fmt === 'table') {
							var tableRows = [];
							while (i < node.body.length && getEffectiveFormat(node, i) === 'table') {
								tableRows.push(node.body[i]);
								i++;
							}
							html += renderTableHtml(tableRows);
						} else {
							var paragraph = node.body[i];

							// Auto-detect Mermaid syntax in body elements (defense-in-depth for docs
							// where the worker didn't set format: "diagram")
							var mermaidMatch = /^(flowchart|sequenceDiagram|graph|classDiagram|stateDiagram|erDiagram|gantt|pie)\b/.test(paragraph.trim());
							if (mermaidMatch || fmt === 'diagram') {
								html += '<div class="jsond-diagram"><pre class="mermaid">' + escapeHtml(paragraph) + '</pre></div>';
								i++;
							} else {
								// text or null — render as paragraph
								var escaped = enhanceDocText(escapeHtml(paragraph), 'html');
								var rendered = '<p' + (paraChanged ? ' class="session-diff"' : '') + '>' + autoLinkUrls(escaped) + '</p>';

								if (i === 0 && node.body.length > 1 && !hasPerElementFormat) {
									html += '<div class="intro-paragraph' + (paraChanged ? ' session-diff' : '') + '">' + rendered + '</div>';
								} else {
									html += rendered;
								}
								i++;
							}
						}
					}
				}
			}
			html += '</div>';
		}

		if (node.links && Array.isArray(node.links) && node.links.length > 0) {
			html += '<div class="jsond-links"><div class="jsond-links-label">Sources</div>';
			node.links.forEach(function (link) {
				html += '<div class="jsond-link-item"><a href="' + escapeHtml(link.url) +
					'" class="jsond-link" target="_blank" rel="noopener">' +
					escapeHtml(link.title || link.url) + '</a></div>';
			});
			html += '</div>';
		}

		if (node.children && Array.isArray(node.children) && node.children.length > 0) {
			html += '<div class="jsond-children">';
			node.children.forEach(function (child, ci) {
				var childCompare = compareNode && compareNode.children && compareNode.children[ci] ? compareNode.children[ci] : null;
				html += processNode(child, childCompare);
			});
			html += '</div>';
		}

		html += '</div>';
		return html;
	}

	var out = '';
	if (Array.isArray(doc.root)) {
		doc.root.forEach(function (node, i) {
			var compareNode = compareDoc && compareDoc.root && compareDoc.root[i] ? compareDoc.root[i] : null;
			out += processNode(node, compareNode);
		});
	}
	return out;
}
